
import { defineComponent } from "vue";
import router from "@/router";
import EnrollmentsService from "@/api/Enrollment/IndividualEnrollmentService";
import store from "@/store";

import StudentsService from "@/api/Aula/StudentsService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import CoursesService from "@/api/Aula/CoursesSevice";

import type {
  IIndividualEnrollmentModel,
  ICoursesModel,
  IAcademicYearModel,
  IStudentsModel,
  IHeadquartersModel,
  ICourseGroupsModel,
} from "@/interfaces/modules";
import { SHOW_PRESCHOOL } from "@/enums";

export default defineComponent({
  name: "EnrollmentsList",
  data() {
    return {
      table: {
        loading: false,
        records: [] as IIndividualEnrollmentModel[],
      } as any,

      academicYears: [] as IAcademicYearModel[],

      form: {
        loading: false,
        dialog: false,
        valid: false,
        record: { Id: 0 } as IIndividualEnrollmentModel & {
          EnrollmentDate?: string;
        },
        rules: {
          required: [(v: any) => !!v || "Este campo es requerido"],
        },
      },

      // grupo actual sobre el que se abrirá la admisión grupal
      currentGroupId: null as number | null,

      // filters (no default = show all)
      filters: {
        studentId: null as number | null,
        headquarterId: null as number | null,
        courseGroupId: null as number | null,
      },

      // full lookup lists from DB
      students: [] as IStudentsModel[],
      headquarters: [] as IHeadquartersModel[],
      courseGroups: [] as ICourseGroupsModel[],
      coursesGroupAvailable: [] as ICourseGroupsModel[],
      courses: [] as ICoursesModel[],

      // inner table headers (StudentId removed)
      groupTableHeaders: [
        {
          key: "Selected",
          label: "",
          sortable: false,
          thStyle: { width: "28px" },
          thClass: "py-0",
          tdClass: "py-0 text-center",
        },
        {
          key: "StudentName",
          label: "Estudiante",
          sortable: true,
          thClass: "py-0",
          tdClass: "py-0",
        },
        {
          key: "EnrollmentDateFormat",
          label: "Fecha admisión",
          sortable: true,
          thClass: "py-0",
          tdClass: "py-0",
        },
        {
          key: "Status",
          label: "Estado",
          sortable: true,
          thClass: "py-0",
          tdClass: "py-0",
        },
        {
          key: "action",
          label: "",
          sortable: false,
          thStyle: { width: "26px" },
          thClass: "py-0",
          tdClass: "py-0 text-center",
        },
      ] as any[],
    };
  },
  computed: {
    studentOptions(): Array<{ value: number; text: string }> {
      return this.students
        .map((s) => ({
          value: s.Id,
          text: `${s.IdentificationNumber || ""} - ${s.Name} ${s.LastName || ""}`.trim(),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    headquarterOptions(): Array<{ value: number; text: string }> {
      return this.headquarters
        .map((h) => ({ value: h.Id, text: h.Description }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    groupOptions(): Array<{ value: number; text: string }> {
      return this.courseGroups
        .map((g) => ({ value: g.Id, text: g.Name }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    filteredItems(): IIndividualEnrollmentModel[] {
      return (this.table.records as any[]).filter((it) => {
        const stOk =
          this.filters.studentId == null ||
          Number(it.StudentId) === Number(this.filters.studentId);
        const hqOk =
          this.filters.headquarterId == null ||
          Number(it.HeadquarterId) === Number(this.filters.headquarterId);
        const cgOk =
          this.filters.courseGroupId == null ||
          Number(it.CourseGroupId) === Number(this.filters.courseGroupId);
        return stOk && hqOk && cgOk;
      });
    },

    groupedByGroup(): Array<{
      groupId: number;
      groupName: string;
      headquarterName?: string;
      academicYearNameDisplay: string;
      items: IIndividualEnrollmentModel[];
      counts: { total: number; enrolled: number; unenrolled: number };
    }> {
      const map = new Map<
        number,
        {
          groupId: number;
          groupName: string;
          headquarterName?: string;
          ayNames: Set<string>;
          items: IIndividualEnrollmentModel[];
        }
      >();

      const norm = (s?: string) => (s || "").trim().toUpperCase();

      for (const it of this.filteredItems as any[]) {
        const gid = Number(it.CourseGroupId || 0);
        if (!map.has(gid)) {
          map.set(gid, {
            groupId: gid,
            groupName: it.CourseGroupName || `Grupo ${gid}`,
            headquarterName: it.HeadquarterName || "",
            ayNames: new Set<string>(),
            items: [],
          });
        }
        const g = map.get(gid)!;
        if (!g.headquarterName && it.HeadquarterName) g.headquarterName = it.HeadquarterName;
        if (it.AcademicYearName) g.ayNames.add(String(it.AcademicYearName));
        g.items.push(it);
      }

      const arr = Array.from(map.values()).map((g) => {
        const total = g.items.length;
        const enrolled = g.items.filter((x: any) => norm(x.Status) === "MATRICULADO").length;
        const unenrolledStrict = g.items.filter(
          (x: any) => norm(x.Status) === "SIN MATRICULAR"
        ).length;
        const unenrolled = unenrolledStrict || Math.max(total - enrolled, 0);
        const ayList = Array.from(g.ayNames).filter(Boolean);
        const academicYearNameDisplay = ayList.length <= 1 ? ayList[0] || "N/D" : "Varios";

        return {
          groupId: g.groupId,
          groupName: g.groupName,
          headquarterName: g.headquarterName || "",
          academicYearNameDisplay,
          items: g.items,
          counts: { total, enrolled, unenrolled },
        };
      });

      arr.sort(
        (a, b) =>
          (a.headquarterName || "").localeCompare(b.headquarterName || "") ||
          (a.groupName || "").localeCompare(b.groupName || "") ||
          a.groupId - b.groupId
      );

      return arr;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    normalizeStatus(s?: string) {
      return (s || "").trim().toUpperCase();
    },

    notify(text: string, type: "success" | "danger" | "warning" | "info" | "secondary") {
      const typeMap: any = { success: "success", danger: "error", warning: "warning", info: "info", secondary: "info" };
      store.dispatch("core/popupDialog", { text, type: typeMap[type] ?? "info", showCancelButton: false });
    },

    async load() {
      this.table.loading = true;
      this.academicYears = (await AcademicYearService.GetAcademicYears()) || [];
      this.courses = (await CoursesService.GetCourses(SHOW_PRESCHOOL.No)) || [];

      const [records, students, headquarters, groups] = await Promise.all([
        EnrollmentsService.GetEnrollments(),
        StudentsService.GetStudents(),
        HeadquartersService.GetHeadquarters(),
        CourseGroupsService.GetCourseGroups(),
      ]);

      this.table.records = records || [];
      this.students = students || [];
      this.headquarters = headquarters || [];
      this.courseGroups = groups || [];

      this.table.loading = false;
    },

    onFiltersChange() {
      // computed props react automatically
    },

    onRowClicked(item: IIndividualEnrollmentModel) {
      this.editEnrollment(item.Id);
    },

    editEnrollment(id: number) {
      router.push(`enrollment-individual/${id}`);
    },

    editStudent(id: number) {
      router.push(`/aula/student-detail/${id}`);
    },

    async handleSubmit(e: Event) {
      e.preventDefault();
      if (this.form.loading) return;

      if (!this.currentGroupId) {
        this.notify("No se encontró el grupo seleccionado.", "danger");
        return;
      }

      const grp = this.groupedByGroup.find((g) => g.groupId === this.currentGroupId);
      if (!grp) {
        this.notify("No se encontró el grupo seleccionado.", "danger");
        return;
      }

      const {
        AcademicYearId,
        HeadquarterId,
        CourseGroupId,
        EnrollmentDate,
      } = this.form.record as any;

      if (!AcademicYearId || !HeadquarterId || !CourseGroupId || !EnrollmentDate) {
        this.notify("Por favor complete todos los campos obligatorios.", "warning");
        return;
      }

      const payloads = grp.items.map((it: any) => ({
        Id: 0,
        StudentId: it.StudentId,
        AcademicYearId,
        HeadquarterId,
        CourseGroupId,
        EnrollmentDate,
        Status: "MATRICULADO",
      }));

      this.form.loading = true;
      try {
        // Ajusta este método si tu servicio usa otro nombre/firma
        for (const p of payloads) {
          await (EnrollmentsService as any).PostEnrollment(p);
        }

        this.notify(
          `${payloads.length} estudiante(s) matriculado(s) correctamente.`,
          "success"
        );
        this.form.dialog = false;
        this.currentGroupId = null;
        await this.load();
      } catch (err) {
        console.error(err);
        this.notify("Ocurrió un error al registrar las admisiones.", "danger");
      } finally {
        this.form.loading = false;
      }
    },

    async openDialog(grp: any) {
      this.currentGroupId = grp.groupId;

      // Preseleccionar SOLO estudiantes sin matrícula
      (grp.items || []).forEach((it: any) => {
        it.Selected = this.normalizeStatus(it.Status) !== "MATRICULADO";
      });

      // 🔹 No auto-rellenar: dejar todo vacío para que el usuario elija
      this.form.record = {
        Id: 0,
        AcademicYearId: null,
        HeadquarterId: null,
        CourseGroupId: null,
        EnrollmentDate: "", // o null si prefieres
      } as any;

      // limpiar lista de grupos disponibles hasta que el usuario seleccione Año + Sede
      this.coursesGroupAvailable = [];

      this.form.dialog = true;
    },

    async changeValuesLoad() {
      const yearId = (this.form.record as any).AcademicYearId || 0;
      const hqId = (this.form.record as any).HeadquarterId || 0;

      if (!yearId || yearId <= 0 || !hqId || hqId <= 0) {
        this.coursesGroupAvailable = [];
        return;
      }

      const courseIdsAtHq = new Set(
        (this.courses || [])
          .filter((c) => c.HeadquarterId === hqId)
          .map((c) => c.Id)
      );

      this.coursesGroupAvailable = (this.courseGroups || [])
        .filter(
          (cg) => cg.AcademicYearId === yearId && courseIdsAtHq.has(cg.CourseId)
        )
        .sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
    },
  },
});
